// R74 — Compact, gedeelde badge-row voor multi-type én multi-leverancier weergave.
// - Slim "Overige" filter is upstream in getOrderTypes() (lib/ordersDecoprof.ts).
// - Max-N badges (desktop=3, mobile=2) + "+N" overflow met Popover (alle items).
// - Subtielere styling (lichte achtergrond, donkere tekst) i.p.v. felle 3D-pills.
import * as React from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { OrderDecoprofType } from "@/lib/ordersDecoprof";

type Variant = "type" | "supplier";

const TYPE_TONE: Record<OrderDecoprofType, string> = {
  Verfbestelling:     "bg-orange-100 text-orange-800 ring-orange-200",
  Behangorder:        "bg-teal-100 text-teal-800 ring-teal-200",
  Spuitbusbestelling: "bg-amber-100 text-amber-800 ring-amber-200",
  Overige:            "bg-pink-100 text-pink-800 ring-pink-200",
};

const SUPPLIER_TONE = "bg-slate-100 text-slate-700 ring-slate-200";
const NEUTRAL_TONE  = "bg-slate-100 text-slate-600 ring-slate-200";

const PILL_BASE =
  "inline-flex items-center justify-center h-5 px-3 py-0.5 text-[11px] font-medium rounded-[10px] ring-1 leading-none whitespace-nowrap";
// R75-fix: type-badges krijgen vaste min-width voor uniforme breedte ongeacht label-lengte.
const PILL_TYPE = `${PILL_BASE} min-w-[140px] text-center`;
const PILL_DEFAULT = PILL_BASE;

function toneFor(variant: Variant, label: string): string {
  if (variant === "type") return TYPE_TONE[label as OrderDecoprofType] ?? NEUTRAL_TONE;
  return SUPPLIER_TONE;
}

function pillFor(variant: Variant): string {
  return variant === "type" ? PILL_TYPE : PILL_DEFAULT;
}

interface Props {
  items: ReadonlyArray<string>;
  variant: Variant;
  className?: string;
}

export const BadgeRow: React.FC<Props> = ({ items, variant, className }) => {
  const isMobile = useIsMobile();
  const max = isMobile ? 2 : 3;

  if (!items || items.length === 0) return null;

  const visible = items.slice(0, max);
  const hidden = items.slice(max);

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {visible.map((label) => (
        <span key={label} className={cn(pillFor(variant), toneFor(variant, label))}>
          {label}
        </span>
      ))}
      {hidden.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                PILL_DEFAULT,
                NEUTRAL_TONE,
                "cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary/40",
              )}
              aria-label={`${hidden.length} meer ${variant === "type" ? "types" : "leveranciers"} tonen`}
            >
              +{hidden.length}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto max-w-[260px] p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap gap-1">
              {hidden.map((label) => (
                <span key={label} className={cn(pillFor(variant), toneFor(variant, label))}>
                  {label}
                </span>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default BadgeRow;
